import { useQuery } from '@tanstack/react-query';
import { apiClient } from '../api/client';

// How many winners the day's top picks should strike, from each pick's price (admin-api
// services/expected-winners.ts). Winners expected, not profit.

export type PriceSource = 'live' | 'tipped';
export type RaceResult = 'scratched' | 'unpriced' | 'pending' | 'won' | 'lost';
export type ExpectationStatus = 'not started' | 'in line' | 'above' | 'below';

export interface RaceExpectation {
  race_id: string;
  race_number: number;
  horse_name: string | null;
  tab_number: number | null;
  price: number | null;
  price_source: PriceSource | null;
  /** 100 / price; null when scratched or unpriced */
  expected_pct: number | null;
  result: RaceResult;
  position: number | null;
}

export interface ExpectationSummary {
  /** races with a priced, unscratched top pick */
  tipped_races: number;
  expected_winners: number;
  expected_strike_pct: number | null;
  /** 10th-90th percentile of winners (Poisson-binomial over the races' chances) */
  range_low: number;
  range_high: number;
  p_zero_pct: number | null;
  settled_races: number;
  actual_winners: number;
  /** expected winners over the settled races only */
  expected_settled: number;
  settled_range_low: number;
  settled_range_high: number;
  status: ExpectationStatus;
}

export interface MeetingExpectation extends ExpectationSummary {
  meeting_id: string;
  track_name: string;
  state: string | null;
  /** not yet released to customers: shown so tips can be checked first */
  is_private: boolean;
  is_training: boolean;
  scratched_races: number;
  races: RaceExpectation[];
}

export interface ExpectedWinnersResponse {
  date: string;
  day: ExpectationSummary;
  meetings: MeetingExpectation[];
}

export function useExpectedWinners(date: string) {
  return useQuery({
    queryKey: ['expected-winners', date],
    queryFn: () => apiClient<ExpectedWinnersResponse>(`/meetings/expected-winners?date=${date}`),
    enabled: !!date,
  });
}
